/*
 * Copyright 2026 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 */
package ai.rules

import kotlinx.serialization.Serializable

/** One primary or sub-category strand. */
@Serializable
data class FlowNode(
    val label: String,
    val amount: Double,
    val pctOfIncome: Double,
    val children: List<FlowNode> = emptyList(),
)

/** One 50/30/20 bucket and the categories feeding it. */
@Serializable
data class BucketNode(
    val bucket: BudgetBucket,
    val amount: Double,
    val pctOfIncome: Double,
    val primaries: List<FlowNode> = emptyList(),
)

/** Income → buckets → primaries → subs, plus whatever income is unspent. */
@Serializable
data class BucketFlow(
    val income: Double,
    val buckets: List<BucketNode>,
    val leftover: Double,
)

/**
 * Builds the Sankey tree for the dashboard's Needs & Wants Flow widget.
 *
 * Pure function, no I/O — mirrors [BudgetHealthAggregator]'s shape so web can
 * call it today and mobile can reuse it later.
 *
 * Classification goes through [BudgetClassificationEngine.effectiveBucket], the
 * same path [BudgetHealthAggregator] uses. BucketFlowBuilderTest asserts the two
 * produce identical bucket totals; that test is what makes a sibling function
 * safe instead of a source of drift.
 *
 * IGNORE is dropped (deliberately outside the framework — internal transfers).
 * UNCLASSIFIED is kept: it's the CTA hook that gets users to fix classifications.
 */
object BucketFlowBuilder {

    /** Buckets shown in the diagram, in render order. */
    private val VISIBLE_BUCKETS = listOf(
        BudgetBucket.NEEDS,
        BudgetBucket.WANTS,
        BudgetBucket.SAVINGS,
        BudgetBucket.UNCLASSIFIED,
    )

    /**
     * @param monthlyIncome the month's ACTUAL income. This is the diagram's ROOT
     *   and the basis for [BucketFlow.leftover]; [SankeyGeometry] sizes every band
     *   from those AMOUNTS and asserts they conserve, so this must stay actual.
     * @param percentBasis the denominator for the `pctOfIncome` LABELS only,
     *   normally the SMOOTHED monthly income (annual estimate ÷ 12) — the
     *   canonical savings denominator from `analytics.savingsRatePercent`.
     *   It exists so one concept doesn't show two numbers on the same screen:
     *   without it the hero savings rate divides by smoothed income while the
     *   Sankey's Saving band divides by the month's actual income, and a
     *   3-paycheque month makes them visibly disagree (28.6% vs 30%).
     *   Resolved exactly the way the canonical helper resolves its denominator:
     *   a non-positive or null basis falls back to [monthlyIncome], so omitting
     *   it reproduces the previous behaviour byte for byte.
     *   Deliberately does NOT touch [BucketFlow.income] or [BucketFlow.leftover] —
     *   only the labels move.
     */
    fun build(
        txns: List<BudgetHealthAggregator.TxnLike>,
        goalAllocations: Double,
        userClassifications: Map<String, BudgetBucket>,
        monthlyIncome: Double,
        percentBasis: Double? = null,
    ): BucketFlow {
        // bucket -> primary -> sub -> amount
        val tree = mutableMapOf<BudgetBucket, MutableMap<String, MutableMap<String, Double>>>()
        val bucketTotals = mutableMapOf<BudgetBucket, Double>()

        for (txn in txns) {
            if (txn.excluded) continue
            if (txn.amount <= 0.0) continue // expenses only; income is the root
            val code = txn.categoryCode ?: continue

            val bucket = BudgetClassificationEngine.effectiveBucket(
                code, txn.detailedCode, userClassifications,
            )
            bucketTotals[bucket] = (bucketTotals[bucket] ?: 0.0) + txn.amount

            val primaries = tree.getOrPut(bucket) { mutableMapOf() }
            val subs = primaries.getOrPut(code) { mutableMapOf() }
            val subKey = txn.detailedCode ?: code
            subs[subKey] = (subs[subKey] ?: 0.0) + txn.amount
        }

        // Goal allocations are savings regardless of per-transaction category —
        // same rule BudgetHealthAggregator applies.
        if (goalAllocations > 0.0) {
            bucketTotals[BudgetBucket.SAVINGS] =
                (bucketTotals[BudgetBucket.SAVINGS] ?: 0.0) + goalAllocations
        }

        // Labels only. `income`/`leftover` below stay on monthlyIncome — the
        // geometry conserves against those amounts.
        val basis = percentBasis?.takeIf { it > 0.0 } ?: monthlyIncome

        fun pct(amount: Double): Double =
            if (basis > 0.0) (amount / basis) * 100.0 else 0.0

        val buckets = VISIBLE_BUCKETS.mapNotNull { bucket ->
            val total = bucketTotals[bucket] ?: 0.0
            if (total <= 0.0) return@mapNotNull null

            val primaries = (tree[bucket] ?: emptyMap()).map { (primary, subs) ->
                val primaryTotal = subs.values.sum()
                FlowNode(
                    label = primary,
                    amount = primaryTotal,
                    pctOfIncome = pct(primaryTotal),
                    children = subs.entries
                        .sortedByDescending { it.value }
                        .map { (sub, amount) ->
                            FlowNode(sub, amount, pct(amount))
                        },
                )
            }.sortedByDescending { it.amount }

            BucketNode(bucket, total, pct(total), primaries)
        }

        val spent = buckets.sumOf { it.amount }
        val leftover = (monthlyIncome - spent).coerceAtLeast(0.0)

        return BucketFlow(income = monthlyIncome, buckets = buckets, leftover = leftover)
    }
}
